import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BillingErrorHandlingService } from '@examdojo/billing';
import { SyllabusQuery } from '@examdojo/category';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { mapToVoid } from '@examdojo/core/rxjs';
import { assertNonNullable } from '@examdojo/core/util/assert';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { QuestionAttemptVoteStoreModel, QuestionGradingPreset, QuestionHttpModel } from '@examdojo/models/question';
import { UserQuery, UserService } from '@examdojo/user';
import { emitOnce } from '@ngneat/elf';
import isEqual from 'lodash/isEqual';
import {
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  finalize,
  first,
  forkJoin,
  map,
  merge,
  Observable,
  of,
  Subject,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { debounceTime, withLatestFrom } from 'rxjs/operators';
import { QuestionAttemptStatusStoreModel } from './models/question-attempt-status.model';
import {
  GradingError,
  isGradingErrorCodeValid,
  isSelfGradableGradingError,
  QuestionAttemptChatStoreModel,
  QuestionAttemptEntityStoreModel,
  QuestionAttemptStoreModel,
  SelfGradableGradingErrorCode,
} from './models/question-attempt.model';
import { QuestionAttemptGradingHttpService } from './question-attempt-grading-http.service';
import { QuestionAttemptHttpService } from './question-attempt-http.service';
import { QuestionAttemptPendingCreationService } from './question-attempt-pending-creation.service';
import { QuestionAttemptResponseImageService } from './question-attempt-response-image';
import { QuestionAttemptVoteHttpService } from './question-attempt-vote-http.service';
import { QuestionAttemptEntityQuery } from './question-attempt.query';
import { QuestionAttemptEntityStore } from './question-attempt.store';
import {
  QuestionGradingPayload,
  QuestionGradingResultHttpModel,
  QuestionGradingResultStoreModel,
} from './question-grading-result.model';
import { mapGradingResultHttpModelToStoreModel } from './question-grading-result.utils';
import { OptionsGradingResult } from './question-v2-grading-result.model';
import { QuestionEntityService } from './question.service';

@Injectable({ providedIn: 'root' })
export class QuestionAttemptEntityService {
  constructor(
    private readonly syllabusQuery: SyllabusQuery,
    private readonly questionAttemptHttpService: QuestionAttemptHttpService,
    private readonly store: QuestionAttemptEntityStore,
    private readonly query: QuestionAttemptEntityQuery,
    private readonly userQuery: UserQuery,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly questionAttemptResponseImageService: QuestionAttemptResponseImageService,
    private readonly questionAttemptGradingHttpService: QuestionAttemptGradingHttpService,
    private readonly questionService: QuestionEntityService,
    private readonly billingErrorHandlingService: BillingErrorHandlingService,
    private readonly userService: UserService,
    private readonly pendingCreationService: QuestionAttemptPendingCreationService,
    private readonly questionAttemptVoteHttpService: QuestionAttemptVoteHttpService,
  ) {
    merge(this.syncPostMCQGradingState(), this.syncPostGradingState()).pipe(takeUntilDestroyed()).subscribe();
  }

  private readonly attemptGraded$$ = new Subject<void>();
  readonly attemptGraded$ = this.attemptGraded$$.asObservable();

  private readonly postGradingStateSyncTrigger$$ = new Subject<QuestionAttemptStoreModel>();

  private readonly postMCQGradingStateSyncTrigger$$ = new Subject<QuestionAttemptStoreModel>();

  setLatestGradedAttemptAsActive(questionId: number) {
    return this.questionAttemptHttpService.fetchAllGradedAttemptsByQuestionId(questionId).pipe(
      switchMap((attempts) => {
        const latestAttempt = attempts[0];

        if (!latestAttempt) {
          return throwError(() => new Error('No graded attempts found'));
        }

        const previousAttemptsIds = attempts
          .filter((attempt) => attempt.id !== latestAttempt.id)
          .map((attempt) => attempt.id);

        return this.fetchQuestionAttemptDetails(
          latestAttempt.id,
          {
            setActive: true,
            resetStore: true,
          },
          previousAttemptsIds,
        );
      }),
    );
  }

  fetchQuestionAttemptDetails(
    questionAttemptId: QuestionAttemptStoreModel['id'] | null = this.query.getActiveId(),
    { setActive, resetStore }: { setActive?: boolean; resetStore?: boolean } = { setActive: false, resetStore: false },
    previousAttemptsIds: Array<QuestionAttemptStoreModel['id']> = [],
  ) {
    if (!questionAttemptId) {
      return of(null);
    }

    if (resetStore) {
      this.resetStore();
    }

    return combineLatest([
      this.fetchQuestionAttempt(questionAttemptId),
      this.syllabusQuery.markCalculationMethod$.pipe(first()),
    ]).pipe(
      switchMap(([attempt, markCalculationMethod]) => {
        if (!attempt) {
          return of(null);
        }

        return forkJoin([
          this.fetchQuestionAttemptStatuses(questionAttemptId),
          this.fetchAttemptGradings(questionAttemptId),
          this.fetchAttemptChat(questionAttemptId),
          this.fetchHasPreviousAttempts(attempt.id, attempt.question_id),
          this.fetchPreviousQuestionAttempts(previousAttemptsIds),
          this.fetchVote(questionAttemptId),
          this.questionAttemptResponseImageService.fetchQuestionAttemptResponseImages(questionAttemptId), // TODO: FF: drop it after new_layout will be enabled for all
          this.questionService.fetchQuestionDetails(attempt.question_id, {
            setActive,
            resetStore,
          }),
        ]).pipe(
          tap(([statuses, gradings, chat, hasPreviousAttempts, previousAttempts, userVote]) => {
            emitOnce(() => {
              this.store.upsert(attempt.id, {
                attempt,
                id: attempt.id,
                markCalculationMethod: markCalculationMethod ?? 'ADDITIVE',
                attemptStatuses: statuses,
                gradings,
                chat,
                hasPreviousAttempts,
                gradingError: this.getGradingError(attempt),
                openCaptureDialog: false,
                userVote,
              });

              previousAttempts.forEach(({ attempt: previousAttempt, gradings: previousAttemptGradings }) => {
                if (!previousAttempt) {
                  return;
                }

                this.store.upsert(previousAttempt.id, {
                  attempt: previousAttempt,
                  id: previousAttempt.id,
                  markCalculationMethod: markCalculationMethod ?? 'ADDITIVE',
                  attemptStatuses: [],
                  gradings: previousAttemptGradings,
                  chat,
                  hasPreviousAttempts,
                  gradingError: this.getGradingError(previousAttempt),
                  openCaptureDialog: false,
                  userVote: null,
                });
              });
            });

            if (setActive) {
              this.store.setActive(attempt.id);
            }
          }),
        );
      }),
    );
  }

  resetStore() {
    this.questionService.resetStore();
    this.questionAttemptResponseImageService.resetStore();
    this.pendingCreationService.clearAll();
    this.store.reset();
  }

  setActive(attemptId: QuestionAttemptStoreModel['id']) {
    this.store.setActive(attemptId);
  }

  fetchQuestionAttempt(id: string | null = this.query.getActiveId()): Observable<QuestionAttemptStoreModel | null> {
    if (!id) {
      return of(null);
    }

    return this.questionAttemptHttpService
      .fetch(id)
      .pipe(this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.question_attempt' }));
  }

  fetchQuestionAttemptStatuses(
    id: string | null = this.query.getActiveId(),
  ): Observable<QuestionAttemptStatusStoreModel[]> {
    if (!id) {
      return of([]);
    }

    return this.questionAttemptHttpService
      .fetchStatuses(id)
      .pipe(this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.question_attempt' }));
  }

  fetchAttemptGradings(
    attemptId: string | null = this.query.getActiveId(),
  ): Observable<QuestionGradingResultStoreModel[]> {
    if (!attemptId) {
      return of([]);
    }

    return this.questionAttemptGradingHttpService.fetchAttemptGradings(attemptId).pipe(
      this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.question_details' }),
      map((gradings: QuestionGradingResultHttpModel[]) =>
        gradings.map((grading) => ({
          id: grading.id,
          created_at: grading.created_at,
          grading_result: mapGradingResultHttpModelToStoreModel(grading.grading_result),
        })),
      ),
    );
  }

  fetchAttemptChat(
    attemptId: string | null = this.query.getActiveId(),
    { updateStore }: { updateStore: boolean } = { updateStore: false },
  ): Observable<QuestionAttemptChatStoreModel | null> {
    if (!attemptId) {
      return of(null);
    }

    return this.questionAttemptHttpService.fetchChats(attemptId).pipe(
      this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.question_attempt' }),
      map((chats) => chats[0] ?? null),
      tap((chat) => {
        if (updateStore) {
          this.store.update(attemptId, (attemptEntity) => ({
            ...attemptEntity,
            chat,
          }));
        }
      }),
    );
  }

  fetchHasPreviousAttempts(currentAttemptId: string, questionId: number): Observable<boolean> {
    if (!questionId) {
      return of(false);
    }

    return this.questionAttemptHttpService
      .hasPreviousAttempts(currentAttemptId, questionId)
      .pipe(this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.question_attempt' }));
  }

  setLocalAttemptStatus(
    status: QuestionAttemptStoreModel['status'],
    attemptId: string | null = this.query.getActiveId(),
  ) {
    if (!attemptId) {
      return;
    }

    this.store.update(attemptId, (attemptEntity) => {
      return {
        ...attemptEntity,
        attempt: {
          ...attemptEntity.attempt,
          status,
        },
      };
    });
  }

  markAllQuestionAttemptsAsSubmittedLocally(
    questionAttempts: QuestionAttemptEntityStoreModel[] = this.query.getAllEntities(),
  ): void {
    emitOnce(() => {
      questionAttempts
        .filter((questionAttempt) => questionAttempt.attempt.status !== 'SUBMITTED')
        .forEach((questionAttempt) => {
          this.setLocalAttemptStatus('SUBMITTED', questionAttempt.id);
        });
    });
  }

  setLocalAttempt(
    attempt: QuestionAttemptStoreModel,
    {
      setActive,
    }: {
      setActive?: boolean;
    } = { setActive: false },
  ) {
    if (!attempt.id) {
      return;
    }

    this.store.update(attempt.id, (attemptEntity) => {
      return {
        ...attemptEntity,
        attempt,
        gradingError: this.getGradingError(attempt) ?? attemptEntity.gradingError,
      };
    });

    if (setActive) {
      this.store.setActive(attempt.id);
    }
  }

  updateQuestionAttemptWithGradings(attemptId: QuestionAttemptStoreModel['id']) {
    return forkJoin([
      this.fetchQuestionAttempt(attemptId),
      this.fetchQuestionAttemptStatuses(attemptId),
      this.fetchAttemptGradings(attemptId),
    ]).pipe(
      tap(([attempt, statuses, gradings]) => {
        if (!attempt) {
          return;
        }

        this.store.update(attemptId, (attemptEntity) => ({
          ...attemptEntity,
          attempt,
          attemptStatuses: statuses,
          gradings,
          gradingError: this.getGradingError(attempt),
          openCaptureDialog: false,
        }));
      }),
    );
  }

  submitForGradingOptionsQuestion(
    selectedOptionIndexes: number[],
    attemptId: string | null = this.query.getActiveId(),
  ) {
    if (!attemptId) {
      return EMPTY;
    }

    const questionAttemptEntity = this.query.getEntity(attemptId);
    if (!questionAttemptEntity) {
      return EMPTY;
    }

    const questionId = questionAttemptEntity.attempt.question_id;
    if (!questionAttemptEntity.gradings.length) {
      return this.gradeMultipleChoiceQuestionAttemptResponse(attemptId, selectedOptionIndexes);
    }

    const userId = this.userQuery.getActiveId();
    assertNonNullable(userId, 'userId');

    this.setLocalAttemptStatus('SUBMITTED', attemptId);

    const freshQuestionAttemptId = this.query.getLatestAttemptIdForQuestionId(questionId);

    if (!freshQuestionAttemptId) {
      // should not happen, button for triggering should be disabled, as new attempt was being created
      return EMPTY;
    }

    this.store.setActive(freshQuestionAttemptId);

    return this.gradeMultipleChoiceQuestionAttemptResponse(freshQuestionAttemptId, selectedOptionIndexes);
  }

  submitForGrading(attemptId: string | null = this.query.getActiveId(), testPreset?: QuestionGradingPreset) {
    if (!attemptId) {
      return EMPTY;
    }
    this.setLocalAttemptStatus('SUBMITTED', attemptId);

    return this.questionAttemptHttpService.triggerGrading(attemptId, testPreset).pipe(
      switchMap(() => {
        const activeUser = this.userQuery.getActive();
        if (activeUser && !activeUser.practice_submitted) {
          return this.userService
            .update(activeUser.id, { practice_submitted: true })
            .pipe(
              this.errorHandlerService.catchError(
                `[QuestionAttemptEntityService.submitForGrading]: Failed to update user practice_submitted flag`,
                () => of(null),
              ),
            );
        }
        return of(null);
      }),
      mapToVoid(),
      this.errorHandlerService.catchError(
        `[QuestionAttemptEntityService.submitForGrading]: Failed to grade the question`,
        (err) => {
          this.billingErrorHandlingService.handleBillingLimitsExceededError(err);

          return this.fetchQuestionAttempt(attemptId).pipe(
            tap((attempt) => {
              if (attempt) {
                this.setLocalAttempt(attempt);
              }
            }),
            switchMap(() => throwError(() => err)),
          );
        },
        {
          context: { attemptId },
          toast: (err) => {
            if (isSelfGradableGradingError(err) || testPreset) {
              return;
            }

            return {
              title: 'Failed to grade the question',
              description: (err as GradingError)?.error_message ?? 'Please try again later',
            };
          },
        },
      ),
    );
  }

  submitForSelfGrading(gradingResult: QuestionGradingPayload, attemptId: string | null = this.query.getActiveId()) {
    if (!attemptId) {
      return EMPTY;
    }
    this.setLocalAttemptStatus('SUBMITTED', attemptId);

    return this.questionAttemptHttpService.triggerSelfGrading(attemptId, gradingResult).pipe(
      this.errorHandlerService.catchError(
        `[QuestionAttemptEntityService.submitForSelfGrading]: Failed to self grade the question`,
        (err) => {
          this.billingErrorHandlingService.handleBillingLimitsExceededError(err);

          return this.fetchQuestionAttempt(attemptId).pipe(
            tap((attempt) => {
              if (attempt) {
                this.setLocalAttempt(attempt);
              }
            }),
            switchMap(() => throwError(() => err)),
          );
        },
        {
          context: { attemptId },
        },
      ),
    );
  }

  synchronizePostGradingState(attempt: QuestionAttemptStoreModel) {
    if (attempt && 'selected_option_indexes' in (attempt.grading_result as unknown as OptionsGradingResult)) {
      this.postMCQGradingStateSyncTrigger$$.next(attempt);
    } else {
      this.postGradingStateSyncTrigger$$.next(attempt);
    }
  }

  setOpenCaptureDialogFlag(open: boolean, attemptId: string | null = this.query.getActiveId()) {
    if (!attemptId) {
      return;
    }

    this.store.updateProp('openCaptureDialog', open);
  }

  setGradingError(gradingError: GradingError, attemptId: string | null = this.query.getActiveId()) {
    if (!attemptId) {
      return;
    }

    this.store.update(attemptId, (attemptEntity) => {
      return {
        ...attemptEntity,
        gradingError,
      };
    });
  }

  precreateMCQAttempt(parentActivityId: string | null) {
    const activeQuestionAttemptEntity = this.query.getActiveUIEntity();

    if (!activeQuestionAttemptEntity || parentActivityId === null) {
      return EMPTY;
    }

    const questionId = activeQuestionAttemptEntity.attempt.question_id;

    return this.createAttemptForQuestion(questionId, activeQuestionAttemptEntity.id).pipe(
      tap((attempt) => {
        if (attempt) {
          this.store.upsert(attempt.id, {
            markCalculationMethod: activeQuestionAttemptEntity.markCalculationMethod,
            attempt,
            id: attempt.id,
            attemptStatuses: [],
            gradings: [],
            chat: activeQuestionAttemptEntity.chat,
            hasPreviousAttempts: activeQuestionAttemptEntity.hasPreviousAttempts,
            gradingError: this.getGradingError(attempt),
            openCaptureDialog: false,
            userVote: null,
          });

          this.store.setActive(attempt.id);
        }
      }),
    );
  }

  createAttemptForQuestion(questionId: QuestionHttpModel['id'], questionAttemptId?: string | null) {
    const userId = this.userQuery.getActiveId();
    assertNonNullable(userId, 'userId');

    if (isNotNullish(questionAttemptId)) {
      this.pendingCreationService.upsertPendingCreation(questionAttemptId, 'recreate_triggered');
    }

    return this.questionAttemptHttpService.create({
      question_id: questionId,
      user_id: userId,
    });
  }

  fetchVote(questionAttemptId: string | null = this.query.getActiveId()) {
    if (!questionAttemptId) {
      return of(null);
    }
    return this.questionAttemptVoteHttpService
      .fetchVoteByQuestionAttemptId(questionAttemptId)
      .pipe(this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.question_attempt_vote' }));
  }

  setVote(
    options: { vote: boolean; textFeedback?: string },
    questionAttemptId: string | null = this.query.getActiveId(),
  ) {
    if (!questionAttemptId) {
      return of(null);
    }

    const questionId = this.query.getEntity(questionAttemptId)?.attempt.question_id;
    if (!questionId) {
      return of(null);
    }

    return this.questionAttemptVoteHttpService.setVote(questionAttemptId, questionId, options).pipe(
      this.errorHandlerService.setHttpErrorMetadata({
        entity: 'examdojo.entity.question_attempt_vote',
        action: 'update',
        context: { questionAttemptId },
      }),
      switchMap(() => this.fetchVote(questionAttemptId)),
      tap((userVote) => this.store.update(questionAttemptId, { userVote })),
    );
  }

  removeVote(voteId: QuestionAttemptVoteStoreModel['id'], questionAttemptId: string | null = this.query.getActiveId()) {
    if (!questionAttemptId) {
      return EMPTY;
    }

    return this.questionAttemptVoteHttpService.delete(voteId).pipe(
      this.errorHandlerService.setHttpErrorMetadata({
        entity: 'examdojo.entity.question_attempt_vote',
        action: 'delete',
        context: { questionAttemptId },
      }),
      tap(() => this.store.update(questionAttemptId, { userVote: null })),
    );
  }

  private fetchPreviousQuestionAttempts(attemptIds: Array<QuestionAttemptStoreModel['id']>) {
    if (attemptIds.length === 0) {
      return of([]);
    }

    return forkJoin(attemptIds.map((id) => this.fetchQuestionAttemptWithGradings(id)));
  }

  private fetchQuestionAttemptWithGradings(id: QuestionAttemptStoreModel['id']) {
    return forkJoin([this.fetchQuestionAttempt(id), this.fetchAttemptGradings(id)]).pipe(
      map(([attempt, gradings]) => {
        return {
          attempt,
          gradings,
        };
      }),
    );
  }

  private gradeMultipleChoiceQuestionAttemptResponse(attemptId: string, selectedOptionIndexes: number[]) {
    if (!attemptId) {
      return EMPTY;
    }

    this.setLocalAttemptStatus('SUBMITTED', attemptId);

    this.pendingCreationService.upsertPendingCreation(attemptId, 'grade_triggered');

    return this.submitForMCQGrading(attemptId, selectedOptionIndexes);
  }

  private submitForMCQGrading(attemptId: string, selectedOptionIndexes: number[]) {
    return this.questionAttemptHttpService.triggerMCQGrading(attemptId, selectedOptionIndexes).pipe(
      switchMap((e) => {
        if (e.status === 201 && e.body?.question_attempt) {
          if ((e.body?.question_attempt.grading_result as unknown as OptionsGradingResult)?.awarded_marks > 0) {
            this.pendingCreationService.clearPendingCreation(attemptId);
          }
          this.synchronizePostGradingState(e.body?.question_attempt);
        }

        return of(null);
      }),
      mapToVoid(),
      this.errorHandlerService.catchError(
        `[QuestionAttemptEntityService.submitForMCQGrading]: Failed to grade the question`,
        (err) => {
          this.billingErrorHandlingService.handleBillingLimitsExceededError(err);

          this.pendingCreationService.clearPendingCreation(attemptId);

          return this.fetchQuestionAttempt(attemptId).pipe(
            tap((attempt) => {
              if (attempt) {
                this.setLocalAttempt(attempt);
              }
            }),
            switchMap(() => throwError(() => err)),
          );
        },
        {
          context: { attemptId },
          toast: (err) => {
            return {
              title: 'Failed to grade the question',
              description: (err as GradingError)?.error_message ?? 'Please try again later',
            };
          },
        },
      ),
    );
  }

  private syncPostMCQGradingState() {
    return this.postMCQGradingStateSyncTrigger$$.pipe(
      distinctUntilChanged(isEqual),
      withLatestFrom(this.syllabusQuery.markCalculationMethod$),
      tap(([attempt, markCalculationMethod]) => {
        this.store.update(attempt.id, (entity: QuestionAttemptEntityStoreModel) => ({
          ...entity,
          attempt,
          markCalculationMethod: markCalculationMethod ?? 'ADDITIVE',
          gradings: [
            {
              grading_result: attempt.grading_result as unknown as OptionsGradingResult,
            } as QuestionGradingResultStoreModel,
          ],
          gradingError: this.getGradingError(attempt),
        }));
      }),
      tap(([attempt]) => {
        if (
          attempt.grading_result &&
          (attempt.grading_result as unknown as OptionsGradingResult)?.awarded_marks &&
          (attempt.grading_result as unknown as OptionsGradingResult)?.awarded_marks > 0
        ) {
          this.attemptGraded$$.next();
        }
      }),
    );
  }

  private syncPostGradingState() {
    return this.postGradingStateSyncTrigger$$.pipe(
      debounceTime(0),
      distinctUntilChanged(isEqual),
      tap(() => {
        this.store.setLoading(true);
      }),
      switchMap((attempt: QuestionAttemptStoreModel) =>
        forkJoin([
          this.syllabusQuery.markCalculationMethod$.pipe(first()),
          this.fetchQuestionAttemptStatuses(attempt.id),
          this.fetchAttemptGradings(attempt.id),
          this.fetchAttemptChat(attempt.id),
          this.fetchHasPreviousAttempts(attempt.id, attempt.question_id),
          this.fetchVote(attempt.id),
          this.questionAttemptResponseImageService.fetchQuestionAttemptResponseImages(attempt.id), // TODO: FF: drop it after new_layout will be enabled for all
        ]).pipe(
          tap(([markCalculationMethod, statuses, gradings, chat, hasPreviousAttempts, userVote]) => {
            this.store.upsert(attempt.id, {
              attempt,
              id: attempt.id,
              markCalculationMethod: markCalculationMethod ?? 'ADDITIVE',
              attemptStatuses: statuses,
              gradings,
              chat,
              hasPreviousAttempts,
              gradingError: this.getGradingError(attempt),
              openCaptureDialog: false,
              userVote,
            });

            this.attemptGraded$$.next();
          }),
          finalize(() => {
            this.store.setLoading(false);
          }),
        ),
      ),
    );
  }

  private getGradingError(attempt: QuestionAttemptStoreModel): GradingError | null {
    if (attempt.status !== 'FAILED') {
      return null;
    }

    const gradingResult = attempt.grading_result as unknown as GradingError | null;

    if (gradingResult?.error_code && isGradingErrorCodeValid(gradingResult.error_code)) {
      return gradingResult;
    }

    return {
      error_code: SelfGradableGradingErrorCode,
      error_message: '',
    };
  }
}
